import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeft, Play, Square, Check, Wind, Sparkles } from 'lucide-react';
import { BACKEND_URL } from '../../utils/api';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import {
  YOGA_STYLES, YOGA_BY_ID, YOGA_BY_PILLAR, PEACE_PILLARS,
} from '../../data/yogaStyles';
import { singingBowl, doubleBowl, speak, stopSpeech, startAirVoidDrone, stopAirVoidDrone } from '../../utils/peaceSounds';
import { pingAuraFuse } from '../../utils/auraPing';
import { YogaSilhouette } from './YogaSilhouette';

/* IDs that have a procedural pose in YogaSilhouette (built-in yogas). */
const POSES_HAS = {
  hatha: true, restorative: true, vinyasa: true, power: true,
  iyengar: true, yin: true, kundalini: true, jivamukti: true,
  ashtanga: true, hot: true, bikram: true, prenatal: true,
};

/* ═══════════════════════════════════════════════════════════════════
   THE PEACE — Bliss Yoga Lifestyle Engine
   Flow: selector → session → reflection → done
   Timer: 20m active + 10m breathing (30m total). No tick sounds.
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#C5A059';
const GOLD_SOFT = '#8B6B2F';
const GOLD_BRIGHT = '#E9C77A';
const BG = '#050308';

/* ── Force customization (zero-cost, all localStorage) ── */
const USER_YOGA_KEY = 'bliss_force_user_yoga_v1';
const TIMER_KEY     = 'bliss_force_timer_v1';
const SCHEDULE_KEY  = 'bliss_force_schedule_v2';
const USER_PILLAR = { color: '#FF9EB8', accent: '#FFC0D3', label: 'YOURS' };

/* Schedule duration presets — mirrors Power tile pattern */
const SCHEDULE_DURATIONS = [
  { id: '1w',   label: '1 Week',   days: 7   },
  { id: '2w',   label: '2 Weeks',  days: 14  },
  { id: '3w',   label: '3 Weeks',  days: 21  },
  { id: '1m',   label: '1 Month',  days: 30  },
  { id: '2m',   label: '2 Months', days: 60  },
  { id: '3m',   label: '3 Months', days: 90  },
  { id: '100d', label: '100 Days', days: 100 },
];

const readLS = (k, dflt) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : dflt; } catch { return dflt; }
};
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ } };

const readUserYoga = () => readLS(USER_YOGA_KEY, []);
const writeUserYoga = (arr) => writeLS(USER_YOGA_KEY, arr);
const readTimer = () => {
  const t = readLS(TIMER_KEY, null);
  return t && t.active > 0 && t.breathing > 0 ? t : { active: 20 * 60, breathing: 10 * 60 };
};
const writeTimer = (t) => writeLS(TIMER_KEY, t);
/* Schedule shape:
   { durationDays: 7, days: { 0: { yogas: [{id,active,breathing}], done: false }, ... } }  */
const readSchedule = () => readLS(SCHEDULE_KEY, null);
const writeSchedule = (s) => writeLS(SCHEDULE_KEY, s);

const fmt = (s) => {
  const m = Math.floor(Math.max(0, s) / 60);
  const r = Math.max(0, s) % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

/* ── Style card in selector grid ── */
const StyleCard = ({ style, active, onSelect, onLongPress }) => {
  const isUser = style.pillar === 'User';
  const pillarColor = isUser ? USER_PILLAR.color : PEACE_PILLARS[style.pillar].color;
  const imgUrl = style.dataUrl || `/yogas/${style.id}.png`;
  const [imgOk, setImgOk] = useState(true);
  const holdRef = useRef(null);
  const startHold = () => {
    if (!onLongPress) return;
    holdRef.current = setTimeout(() => onLongPress(style), 650);
  };
  const cancelHold = () => { if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null; } };
  return (
    <button
      type="button"
      onClick={onSelect}
      onPointerDown={startHold}
      onPointerUp={cancelHold}
      onPointerLeave={cancelHold}
      onContextMenu={(e) => e.preventDefault()}
      data-testid={`peace-style-${style.id}`}
      className="relative flex flex-col items-center rounded-xl overflow-hidden active:scale-95 transition-transform"
      style={{
        background: `linear-gradient(160deg, rgba(255,255,255,0.05), rgba(0,0,0,0.4))`,
        border: active ? `1.5px solid ${GOLD_BRIGHT}` : `1px solid ${pillarColor}55`,
        boxShadow: active
          ? `0 0 20px ${GOLD}66, inset 0 0 12px rgba(255,255,255,0.05)`
          : `0 0 10px ${pillarColor}22`,
        aspectRatio: '1 / 1.15',
      }}
    >
      <div className="flex-1 w-full flex items-center justify-center overflow-hidden relative"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(20,15,10,0.5), rgba(0,0,0,0.85) 75%)',
          border: `1px solid ${GOLD}55`,
          boxShadow: `inset 0 0 12px ${GOLD}22`,
          borderRadius: '10px 10px 0 0',
          margin: 1,
        }}>
        {style.dataUrl ? (
          <img src={style.dataUrl} alt={style.name} loading="lazy"
            className="w-full h-full object-cover"
            style={{ filter: `drop-shadow(0 0 8px ${pillarColor}88)`,
                     transform: 'scale(1.12)', transformOrigin: 'center' }} />
        ) : imgOk ? (
          <img src={imgUrl} alt={style.name} loading="lazy" onError={() => setImgOk(false)}
            className="w-full h-full object-cover"
            style={{ filter: `drop-shadow(0 0 8px ${pillarColor}88)`,
                     transform: 'scale(1.12)', transformOrigin: 'center' }} />
        ) : POSES_HAS[style.id] ? (
          <YogaSilhouette yogaId={style.id} size={120} stroke={pillarColor === USER_PILLAR.color ? GOLD_BRIGHT : pillarColor}/>
        ) : (
          // Fallback: SVG lotus placeholder in pillar color
          <svg width="60%" height="60%" viewBox="0 0 60 60"
            style={{ filter: `drop-shadow(0 0 6px ${pillarColor}aa)` }}>
            <g fill="none" stroke={pillarColor} strokeWidth="1.4" strokeLinecap="round">
              <path d="M30 42 C 18 42, 12 34, 12 26 C 18 26, 24 30, 30 34 C 36 30, 42 26, 48 26 C 48 34, 42 42, 30 42 Z"/>
              <path d="M30 34 C 22 30, 18 22, 20 12 C 26 16, 30 22, 30 28 Z"/>
              <path d="M30 34 C 38 30, 42 22, 40 12 C 34 16, 30 22, 30 28 Z"/>
              <circle cx="30" cy="46" r="3.5"/>
            </g>
          </svg>
        )}
      </div>
      <div className="w-full text-center py-2 px-1"
        style={{ background: `linear-gradient(180deg, transparent, ${pillarColor}22)` }}>
        <p className="font-['Cinzel'] uppercase tracking-[0.18em]"
          style={{ color: GOLD_BRIGHT, fontSize: 11 }}>{style.name}</p>
        <p className="font-['Outfit'] uppercase tracking-[0.14em] mt-0.5"
          style={{ color: pillarColor, fontSize: 7 }}>{style.pillar}</p>
      </div>
      {active && (
        <span className="absolute top-1 right-1 rounded-full flex items-center justify-center"
          style={{ width: 18, height: 18, background: GOLD, color: '#0a0a0a' }}>
          <Check size={11} strokeWidth={3}/>
        </span>
      )}
    </button>
  );
};

/* ── Preset session durations (active + breathing) ── */
const TIMER_PRESETS = [
  { a: 5,  b: 5,  label: '5+5' },
  { a: 10, b: 5,  label: '10+5' },
  { a: 15, b: 5,  label: '15+5' },
  { a: 20, b: 5,  label: '20+5' },
  { a: 20, b: 10, label: '20+10' },
  { a: 30, b: 10, label: '30+10' },
  { a: 45, b: 10, label: '45+10' },
  { a: 60, b: 10, label: '60+10' },
];

/* ── Selector screen ── */
const Selector = ({ selected, onSelect, onBegin, stats, onOpenSchedule, onOpenFuse, onOpenForge, userYoga, onDeleteUser, timer, onEditTimer }) => {
  const [pillarFilter, setPillarFilter] = useState('all');
  const [editingTime, setEditingTime] = useState(false);

  const styles = useMemo(() => {
    if (pillarFilter === 'all') return [...YOGA_STYLES, ...userYoga];
    if (pillarFilter === 'User') return userYoga;
    return YOGA_BY_PILLAR[pillarFilter];
  }, [pillarFilter, userYoga]);

  const totalMin = Math.round((timer.active + timer.breathing) / 60);
  const activeMin = Math.round(timer.active / 60);
  const breathMin = Math.round(timer.breathing / 60);
  const activePreset = TIMER_PRESETS.find(p => p.a === activeMin && p.b === breathMin);

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="peace-selector">
      <div className="flex-shrink-0 px-3 pt-2 pb-2 text-center">
        <p className="font-['Cinzel'] tracking-[0.32em]"
          style={{ color: GOLD_BRIGHT, fontSize: 13, textShadow: `0 0 12px ${GOLD}55` }}>
          BLISS FORCE
        </p>
        {!editingTime ? (
          <button type="button" onClick={() => setEditingTime(true)}
            data-testid="peace-timer-edit-btn"
            className="mt-1.5 font-['Outfit'] uppercase tracking-[0.24em] mx-auto rounded-lg px-3 py-1.5"
            style={{
              color: GOLD_BRIGHT, fontSize: 9,
              background: 'rgba(197,160,89,0.06)',
              border: `1px solid ${GOLD}66`,
              boxShadow: `0 0 8px ${GOLD}22, inset 0 0 6px ${GOLD}15`,
            }}>
            {totalMin} · MIN · SESSION · {activeMin} · ACTIVE · {breathMin} · BREATH · ✎
          </button>
        ) : (
          <div className="mt-1 flex flex-wrap justify-center gap-1" data-testid="peace-timer-edit">
            {TIMER_PRESETS.map((p) => {
              const on = activePreset?.label === p.label;
              return (
                <button key={p.label} type="button"
                  data-testid={`peace-timer-preset-${p.label}`}
                  onClick={() => { onEditTimer({ active: p.a * 60, breathing: p.b * 60 }); setEditingTime(false); }}
                  className="rounded-full px-2 py-0.5 font-['Cinzel'] uppercase tracking-[0.14em] transition-all"
                  style={{
                    background: on ? `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})` : 'rgba(255,255,255,0.04)',
                    color: on ? '#0a0a0a' : GOLD_BRIGHT,
                    border: `1px solid ${GOLD}${on ? 'ff' : '55'}`,
                    fontSize: 9,
                    boxShadow: on ? `0 0 8px ${GOLD}77` : 'none',
                  }}>{p.label}</button>
              );
            })}
            <button type="button" onClick={() => setEditingTime(false)}
              data-testid="peace-timer-close-btn"
              className="rounded-full px-2 py-0.5 font-['Outfit'] uppercase tracking-[0.14em]"
              style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)',
                       border: '1px solid rgba(255,255,255,0.1)', fontSize: 8 }}>✕</button>
          </div>
        )}
      </div>

      {/* Stats + Schedule + Fuse CTAs (5 columns) */}
      <div className="flex-shrink-0 px-3 pb-2 grid grid-cols-5 gap-1.5" data-testid="peace-stats">
        <StatChip label="Sessions" value={stats?.total_sessions || 0}/>
        <StatChip label="Minutes"  value={stats?.total_minutes || 0}/>
        <StatChip label="Streak"   value={`${stats?.streak_days || 0}d`}/>
        <button onClick={onOpenSchedule} data-testid="peace-open-schedule-btn"
          className="rounded-lg px-1 py-1.5 text-center transition-all"
          style={{ background: 'rgba(197,160,89,0.10)', border: `1px solid ${GOLD}55`,
                   boxShadow: `0 0 6px ${GOLD}22` }}>
          <p className="font-['Cinzel']" style={{ color: GOLD_BRIGHT, fontSize: 13, lineHeight: 1 }}>✎</p>
          <p className="font-['Outfit'] uppercase tracking-[0.14em] mt-0.5"
            style={{ color: GOLD_SOFT, fontSize: 7 }}>Schedule</p>
        </button>
        <button onClick={onOpenFuse} data-testid="peace-open-fuse-btn"
          className="rounded-lg px-1 py-1.5 text-center transition-all"
          style={{ background: 'rgba(197,160,89,0.18)', border: `1px solid ${GOLD_BRIGHT}`,
                   boxShadow: `0 0 10px ${GOLD}44` }}>
          <p className="font-['Cinzel']" style={{ color: GOLD_BRIGHT, fontSize: 13, lineHeight: 1 }}>▤</p>
          <p className="font-['Outfit'] uppercase tracking-[0.14em] mt-0.5"
            style={{ color: GOLD_SOFT, fontSize: 7 }}>Fuse</p>
        </button>
      </div>

      {/* Pillar filter tabs — fit All→User in one row */}
      <div className="flex-shrink-0 px-3 pb-2">
        <div className="flex gap-1">
          <PillarChip label="All" active={pillarFilter === 'all'} onClick={() => setPillarFilter('all')} testid="pf-all"/>
          {Object.keys(PEACE_PILLARS).map(p => (
            <PillarChip key={p} label={p} active={pillarFilter === p} onClick={() => setPillarFilter(p)}
              color={PEACE_PILLARS[p].color} testid={`pf-${p.toLowerCase()}`}/>
          ))}
          <PillarChip label="User" active={pillarFilter === 'User'} onClick={() => setPillarFilter('User')}
            color={USER_PILLAR.color} testid="pf-user"/>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
        <div className="grid grid-cols-3 gap-2">
          {styles.map(s => (
            <StyleCard key={s.id} style={s}
              active={selected === s.id} onSelect={() => onSelect(s.id)}
              onLongPress={s.pillar === 'User' ? (() => {
                if (window.confirm(`Delete "${s.name}"?`)) onDeleteUser(s.id);
              }) : undefined}/>
          ))}
          {pillarFilter === 'User' && (
            <button type="button" onClick={onOpenForge} data-testid="peace-forge-btn"
              className="relative flex flex-col items-center justify-center rounded-xl overflow-hidden active:scale-95 transition-transform"
              style={{
                background: `linear-gradient(160deg, rgba(255,158,184,0.10), rgba(0,0,0,0.4))`,
                border: `1.5px dashed ${USER_PILLAR.color}88`,
                boxShadow: `0 0 12px ${USER_PILLAR.color}22`,
                aspectRatio: '1 / 1.15',
              }}>
              <p className="font-['Cinzel']" style={{ color: USER_PILLAR.color, fontSize: 30, lineHeight: 1 }}>+</p>
              <p className="font-['Outfit'] uppercase tracking-[0.22em] mt-1"
                style={{ color: USER_PILLAR.color, fontSize: 8 }}>Forge</p>
              <p className="font-['Cormorant_Garamond'] italic mt-1"
                style={{ color: `${USER_PILLAR.color}CC`, fontSize: 9 }}>Your own yoga</p>
            </button>
          )}
        </div>
      </div>

      {/* Begin CTA */}
      <div className="flex-shrink-0 px-3 pt-2 border-t"
        style={{
          borderColor: `${GOLD}22`,
          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))',
          paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 56px)',
        }}>
        <button
          type="button"
          disabled={!selected}
          onClick={onBegin}
          data-testid="peace-begin-btn"
          className="w-full rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.32em] transition-all disabled:opacity-40"
          style={{
            background: selected
              ? `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`
              : 'rgba(255,255,255,0.04)',
            color: selected ? '#0a0a0a' : 'rgba(255,255,255,0.4)',
            border: `1px solid ${GOLD}`,
            fontSize: 12,
            boxShadow: selected ? `0 0 20px ${GOLD}66` : 'none',
          }}>
          Begin · {totalMin} min
        </button>
        {selected && (
          <p className="text-center mt-2 font-['Outfit'] italic"
            style={{ color: GOLD_SOFT, fontSize: 10 }}>
            &ldquo;{YOGA_BY_ID[selected]?.focus}&rdquo;
          </p>
        )}
      </div>
    </div>
  );
};

const StatChip = ({ label, value }) => (
  <div className="rounded-lg px-1 py-1.5 text-center"
    style={{ background: 'rgba(197,160,89,0.06)', border: `1px solid ${GOLD}33` }}>
    <p className="font-['Cinzel']" style={{ color: GOLD_BRIGHT, fontSize: 13, lineHeight: 1 }}>{value}</p>
    <p className="font-['Outfit'] uppercase tracking-[0.14em] mt-0.5"
      style={{ color: GOLD_SOFT, fontSize: 7 }}>{label}</p>
  </div>
);

const PillarChip = ({ label, active, onClick, color = GOLD, testid }) => (
  <button
    type="button" onClick={onClick} data-testid={testid}
    className="flex-1 min-w-0 rounded-full px-1 py-1.5 font-['Outfit'] uppercase tracking-[0.14em] transition-all truncate"
    style={{
      background: active ? `linear-gradient(180deg, ${color}, ${color}77)` : 'rgba(255,255,255,0.03)',
      color: active ? '#0a0a0a' : `${color}dd`,
      border: `1px solid ${color}${active ? 'ff' : '55'}`,
      fontSize: 8,
    }}>{label}</button>
);

/* ── Session screen (timer + breathing animation) ── */
const Session = ({ style, timer, onEnd, onCancel, onFuseNow }) => {
  const [phase, setPhase] = useState('active'); // 'active' | 'breathing'
  const [remaining, setRemaining] = useState(timer.active);
  const [running, setRunning] = useState(true);
  const [breathIn, setBreathIn] = useState(true);
  const startedAtRef = useRef(Date.now());
  const activeElapsedRef = useRef(0);
  const breathingElapsedRef = useRef(0);
  const isUser = style.pillar === 'User';
  const pillarColor = isUser ? USER_PILLAR.color : PEACE_PILLARS[style.pillar].color;
  const imgUrl = style.dataUrl || `/yogas/${style.id}.png`;
  const [imgOk, setImgOk] = useState(true);

  // Session-open cue: single bowl + spoken opener + continuous air+void drone
  useEffect(() => {
    singingBowl();
    setTimeout(() => speak(`${style.name} yoga. Begin.`), 400);
    startAirVoidDrone();
    return () => { stopSpeech(); stopAirVoidDrone(); };
  }, [style.name]);

  // Main countdown tick (no sound each second) — deadline-based, never skips.
  const remainingRef = useRef(remaining);
  remainingRef.current = remaining;
  useEffect(() => {
    if (!running) return;
    const startLeft = remainingRef.current;
    const endAt = performance.now() + startLeft * 1000;
    let shown = startLeft;
    const iv = setInterval(() => {
      const left = Math.max(0, Math.ceil((endAt - performance.now()) / 1000));
      if (left === shown) return;
      const delta = shown - left;
      shown = left;
      if (phase === 'active') activeElapsedRef.current += delta;
      else breathingElapsedRef.current += delta;
      if (left > 0) { setRemaining(left); return; }
      clearInterval(iv);
      if (phase === 'active') {
        activeElapsedRef.current = timer.active;
        setPhase('breathing');
        singingBowl();
        setTimeout(() => speak(`Active phase complete. ${Math.round(timer.breathing / 60)} minutes of deep breathing now.`), 500);
        setRemaining(timer.breathing);
        return;
      }
      breathingElapsedRef.current = timer.breathing;
      setRemaining(0);
      setRunning(false);
      stopAirVoidDrone();
      doubleBowl();
      setTimeout(() => speak('Session complete. Namaste.'), 700);
      setTimeout(() => {
        onEnd({
          completed: true,
          active_sec: timer.active,
          breathing_sec: timer.breathing,
          duration_sec: timer.active + timer.breathing,
        });
      }, 2500);
    }, 100);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, phase, onEnd]);

  // Breathing bubble — 4s in / 4s out
  useEffect(() => {
    if (phase !== 'breathing') return;
    const iv = setInterval(() => setBreathIn(v => !v), 4000);
    return () => clearInterval(iv);
  }, [phase]);

  const handleEndEarly = () => {
    stopSpeech();
    stopAirVoidDrone();
    singingBowl();
    onEnd({
      completed: false,
      active_sec: activeElapsedRef.current,
      breathing_sec: breathingElapsedRef.current,
      duration_sec: activeElapsedRef.current + breathingElapsedRef.current,
    });
  };

  const totalRemaining = phase === 'active'
    ? remaining + timer.breathing
    : remaining;

  // Progress % of the whole session (active + breathing). Live-updates every tick.
  const totalDuration = timer.active + timer.breathing;
  const elapsedTotal = phase === 'active'
    ? (timer.active - remaining)
    : (timer.active + (timer.breathing - remaining));
  const percentDone = totalDuration > 0
    ? Math.floor((elapsedTotal / totalDuration) * 100)
    : 0;
  const canFuse = percentDone >= 51;

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="peace-session">
      <div className="flex-shrink-0 px-3 pt-2 pb-2 text-center">
        <p className="font-['Cinzel'] tracking-[0.24em]"
          style={{ color: pillarColor, fontSize: 12 }}>
          {style.name.toUpperCase()}
        </p>
        <p className="font-['Outfit'] uppercase tracking-[0.3em] mt-0.5"
          style={{ color: GOLD_SOFT, fontSize: 8 }}>
          {phase === 'active' ? 'Active · Postures' : 'Breathing · Deep Reset'}
        </p>
      </div>

      {/* Visual body */}
      <div className="flex-1 min-h-0 flex flex-col items-center px-3 pb-1 overflow-hidden">
        {phase === 'active' ? (
          <>
            {/* IMAGE — bigger square that scales with viewport (was
                capped at 220 px which felt small on modern phones). */}
            <div className="flex items-center justify-center rounded-xl mt-1 mx-auto flex-shrink-0 overflow-hidden"
              style={{
                background: '#000000',
                border: `1.5px solid ${GOLD}`,
                boxShadow: `0 0 22px ${GOLD}44`,
                padding: 4,
                width: 'min(78vw, 380px)',
                height: 'min(78vw, 380px)',
              }}>
              {style.dataUrl ? (
                <img src={style.dataUrl} alt={style.name}
                  className="w-full h-full object-cover"
                  style={{ transform: 'scale(1.12)', transformOrigin: 'center' }} />
              ) : imgOk ? (
                <img src={imgUrl} alt={style.name}
                  onError={() => setImgOk(false)}
                  className="w-full h-full object-cover"
                  style={{ transform: 'scale(1.12)', transformOrigin: 'center' }} />
              ) : POSES_HAS[style.id] ? (
                <YogaSilhouette yogaId={style.id} size={220}
                  stroke={isUser ? GOLD_BRIGHT : pillarColor}/>
              ) : (
                <svg width="160" height="160" viewBox="0 0 60 60"
                  style={{ filter: `drop-shadow(0 0 18px ${pillarColor}bb)` }}>
                  <g fill="none" stroke={pillarColor} strokeWidth="1.4" strokeLinecap="round">
                    <path d="M30 42 C 18 42, 12 34, 12 26 C 18 26, 24 30, 30 34 C 36 30, 42 26, 48 26 C 48 34, 42 42, 30 42 Z"/>
                    <path d="M30 34 C 22 30, 18 22, 20 12 C 26 16, 30 22, 30 28 Z"/>
                    <path d="M30 34 C 38 30, 42 22, 40 12 C 34 16, 30 22, 30 28 Z"/>
                    <circle cx="30" cy="46" r="3.5"/>
                  </g>
                </svg>
              )}
            </div>

            {/* INSTRUCTION + KEY ACTION — pillar-colored notes.
                `mt-auto` pushes them to the BOTTOM of the flex column
                so they sit directly above the timer strip with no
                large empty gap in between. */}
            <div className="w-full space-y-1.5 pb-1 flex-shrink-0 mt-auto" data-testid="peace-notes">
              <div className="rounded-lg px-2.5 py-1.5"
                style={{
                  background: `linear-gradient(135deg, ${pillarColor}18, ${pillarColor}08)`,
                  border: `1px solid ${pillarColor}77`,
                  boxShadow: `0 0 10px ${pillarColor}33`,
                }}
                data-testid="peace-instruction-box">
                <p className="font-['Outfit'] uppercase tracking-[0.24em] mb-0.5"
                  style={{ color: pillarColor, fontSize: 7 }}>Instruction</p>
                <p className="font-['Outfit']"
                  style={{ color: 'rgba(255,255,255,0.92)', fontSize: 11, lineHeight: 1.35 }}>
                  {style.instruction}
                </p>
              </div>
              <div className="rounded-lg px-2.5 py-1.5"
                style={{
                  background: `linear-gradient(135deg, ${GOLD}18, ${GOLD}06)`,
                  border: `1px solid ${GOLD_BRIGHT}99`,
                  boxShadow: `0 0 10px ${GOLD}33`,
                }}
                data-testid="peace-keyaction-box">
                <p className="font-['Outfit'] uppercase tracking-[0.24em] mb-0.5"
                  style={{ color: GOLD_BRIGHT, fontSize: 7 }}>Key Action</p>
                <p className="font-['Outfit']"
                  style={{ color: 'rgba(255,255,255,0.92)', fontSize: 11, lineHeight: 1.35 }}>
                  {style.key_action}
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Breathing bubble — expands on inhale, contracts on exhale */}
            <div className="relative flex items-center justify-center mt-4"
              style={{ width: 240, height: 240 }}>
              <div
                data-testid="peace-breath-bubble"
                style={{
                  width: breathIn ? 220 : 120,
                  height: breathIn ? 220 : 120,
                  borderRadius: '50%',
                  background: `radial-gradient(circle at 40% 35%, ${GOLD_BRIGHT}55, ${pillarColor}22 55%, transparent 78%)`,
                  border: `1.5px solid ${GOLD_BRIGHT}88`,
                  boxShadow: `0 0 40px ${pillarColor}aa, inset 0 0 30px ${GOLD}55`,
                  transition: 'width 4s ease-in-out, height 4s ease-in-out',
                }}
              />
              <Wind size={38}
                className="absolute"
                style={{ color: GOLD_BRIGHT, opacity: 0.85,
                  filter: `drop-shadow(0 0 10px ${GOLD}aa)` }} />
            </div>
            <p className="font-['Cinzel'] tracking-[0.35em] mt-6"
              style={{ color: GOLD_BRIGHT, fontSize: 13 }}>
              {breathIn ? 'INHALE' : 'EXHALE'}
            </p>
            <p className="font-['Outfit'] uppercase tracking-[0.2em] mt-1"
              style={{ color: GOLD_SOFT, fontSize: 9 }}>
              4 seconds in · 4 seconds out
            </p>
          </>
        )}
      </div>

      {/* Timer strip — safe-area floor guarantees the End Session button
          stays clear of the Android nav bar / iOS home indicator on
          every phone. */}
      <div className="flex-shrink-0 px-3 pt-1.5 border-t"
        style={{
          borderColor: `${GOLD}22`,
          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))',
          paddingBottom: 'calc(max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 28px) + 16px)',
        }}>
        <div className="flex items-baseline justify-center gap-3 mb-1">
          <p className="font-['Cinzel']"
            style={{ color: GOLD_BRIGHT, fontSize: 26, letterSpacing: '0.15em',
              textShadow: `0 0 18px ${GOLD}66` }}
            data-testid="peace-timer-phase">
            {fmt(remaining)}
          </p>
          <p className="font-['Outfit'] uppercase tracking-[0.24em]"
            style={{ color: GOLD_SOFT, fontSize: 8 }}
            data-testid="peace-timer-total">
            of {fmt(totalRemaining)} total
          </p>
        </div>
        {/* Phase progress bar */}
        <div className="w-full h-1 rounded-full mb-1.5"
          style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div className="h-full rounded-full transition-all"
            style={{
              width: `${100 - (remaining / (phase === 'active' ? timer.active : timer.breathing)) * 100}%`,
              background: `linear-gradient(90deg, ${pillarColor}, ${GOLD_BRIGHT})`,
              boxShadow: `0 0 10px ${pillarColor}aa`,
            }}/>
        </div>
        <button
          type="button"
          onClick={handleEndEarly}
          data-testid="peace-end-early-btn"
          className="w-full rounded-lg py-1.5 font-['Outfit'] uppercase tracking-[0.24em] flex items-center justify-center gap-2 mb-1.5"
          style={{
            background: 'rgba(200,80,80,0.08)',
            border: '1px solid rgba(216,121,121,0.45)',
            color: '#D87979',
            fontSize: 10,
          }}>
          <Square size={12}/> End Session Early
        </button>
        {onFuseNow && (
          <button
            type="button"
            onClick={() => { if (canFuse) { stopAirVoidDrone(); onFuseNow(style, {
              active_sec: activeElapsedRef.current || timer.active,
              breathing_sec: breathingElapsedRef.current || timer.breathing,
            }); } }}
            disabled={!canFuse}
            data-testid="peace-fuse-now-btn"
            className="w-full rounded-lg py-1.5 font-['Cinzel'] uppercase tracking-[0.28em] flex items-center justify-center gap-2 transition-all"
            style={{
              background: canFuse
                ? `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`
                : 'rgba(255,255,255,0.04)',
              border: `1px solid ${canFuse ? GOLD : 'rgba(255,255,255,0.15)'}`,
              color: canFuse ? '#0a0a0a' : 'rgba(255,255,255,0.4)',
              fontSize: 10,
              boxShadow: canFuse ? `0 0 14px ${GOLD}55` : 'none',
              cursor: canFuse ? 'pointer' : 'not-allowed',
              opacity: canFuse ? 1 : 0.55,
            }}>
            {canFuse
              ? '✦ Fuse This Yoga'
              : `✦ Fuse Locked · ${percentDone}% / 51%`}
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Reflection screen ── */
const Reflection = ({ style, sessionData, onSubmit, onSkip }) => {
  const [reflection, setReflection] = useState('');
  const [clarity, setClarity] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (saving) return;
    setSaving(true);
    await onSubmit({ reflection: reflection.trim(), clarity_rating: clarity });
    setSaving(false);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto px-4 pt-3 scrollbar-hide" data-testid="peace-reflection"
      style={{ paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 56px)' }}>
      <div className="text-center mb-4">
        <p className="font-['Cinzel'] tracking-[0.28em]"
          style={{ color: GOLD_BRIGHT, fontSize: 16, textShadow: `0 0 16px ${GOLD}55` }}>
          NAMASTE
        </p>
        <p className="font-['Outfit'] italic mt-1"
          style={{ color: GOLD_SOFT, fontSize: 11 }}>
          {sessionData.completed
            ? `${style.name} session completed · ${Math.round(sessionData.duration_sec / 60)} min`
            : `${style.name} session · ${Math.round(sessionData.duration_sec / 60)} min recorded`}
        </p>
      </div>

      {/* Free-form reflection */}
      <label className="font-['Outfit'] uppercase tracking-[0.24em] mb-2"
        style={{ color: GOLD, fontSize: 10 }}>
        What did you do today?
      </label>
      <textarea
        value={reflection}
        onChange={(e) => setReflection(e.target.value.slice(0, 1000))}
        placeholder="Share what came up during your practice…"
        rows={4}
        data-testid="peace-reflection-text"
        className="w-full rounded-lg px-3 py-2.5 font-['Outfit'] resize-none"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${GOLD}44`,
          color: 'rgba(255,255,255,0.9)',
          fontSize: 13,
          outline: 'none',
        }}/>
      <p className="text-right font-['Outfit'] mt-1"
        style={{ color: GOLD_SOFT, fontSize: 9 }}>{reflection.length}/1000</p>

      {/* Clarity rating 1-10 */}
      <label className="font-['Outfit'] uppercase tracking-[0.24em] mb-2 mt-4"
        style={{ color: GOLD, fontSize: 10 }}>
        Mental Clarity · 1 to 10
      </label>
      <div className="grid grid-cols-10 gap-1 mb-1">
        {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
          <button key={n} type="button"
            onClick={() => setClarity(n)}
            data-testid={`peace-clarity-${n}`}
            className="rounded-md py-2 font-['Cinzel'] transition-all"
            style={{
              background: clarity === n
                ? `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`
                : 'rgba(255,255,255,0.04)',
              color: clarity === n ? '#0a0a0a' : `${GOLD}bb`,
              border: `1px solid ${GOLD}${clarity === n ? 'ff' : '33'}`,
              fontSize: 11,
              boxShadow: clarity === n ? `0 0 12px ${GOLD}88` : 'none',
            }}>{n}</button>
        ))}
      </div>
      <div className="flex justify-between font-['Outfit'] uppercase tracking-[0.2em] mb-4"
        style={{ color: GOLD_SOFT, fontSize: 8 }}>
        <span>Foggy</span><span>Crystal</span>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-auto">
        <button type="button" onClick={onSkip}
          data-testid="peace-skip-reflection"
          className="flex-1 rounded-lg py-3 font-['Outfit'] uppercase tracking-[0.22em]"
          style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: 'rgba(255,255,255,0.55)',
            fontSize: 10,
          }}>Skip</button>
        <button type="button" onClick={handleSubmit} disabled={saving}
          data-testid="peace-save-reflection"
          className="flex-[2] rounded-lg py-3 font-['Cinzel'] uppercase tracking-[0.28em] disabled:opacity-50"
          style={{
            background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
            color: '#0a0a0a',
            border: `1px solid ${GOLD}`,
            fontSize: 11,
            boxShadow: `0 0 16px ${GOLD}66`,
          }}>
          {saving ? 'Saving…' : 'Seal · Save'}
        </button>
      </div>
    </div>
  );
};

/* ── FORGE YOGA — user creates their own yoga style (image + name + brief) ── */
const ForgeYoga = ({ onSave, onCancel }) => {
  const [name, setName] = useState('');
  const [brief, setBrief] = useState('');
  const [instruction, setInstruction] = useState('');
  const [dataUrl, setDataUrl] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Resize to fit ~256px for reasonable localStorage footprint
    const img = document.createElement('img');
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const max = 256;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const cvs = document.createElement('canvas');
        cvs.width = w; cvs.height = h;
        cvs.getContext('2d').drawImage(img, 0, 0, w, h);
        setDataUrl(cvs.toDataURL('image/jpeg', 0.7));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const canSave = name.trim().length >= 2;
  const save = () => {
    const id = `user_${Date.now()}`;
    onSave({
      id, pillar: 'User',
      name: name.trim(),
      tagline: brief.trim() || 'Your own forged yoga',
      instruction: instruction.trim() || 'Follow your own inner sequence.',
      key_action: instruction.trim() || 'Move with intention.',
      dataUrl,
    });
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="peace-forge-form">
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-3 pb-4">
        <p className="font-['Cinzel'] tracking-[0.24em] text-center"
          style={{ color: USER_PILLAR.color, fontSize: 14 }}>FORGE YOUR YOGA</p>
        <p className="font-['Cormorant_Garamond'] italic text-center mb-4"
          style={{ color: `${USER_PILLAR.color}99`, fontSize: 11 }}>
          Bind your own practice into the Force library
        </p>

        {/* Image picker */}
        <button type="button" onClick={() => fileRef.current?.click()}
          data-testid="peace-forge-image-btn"
          className="w-full rounded-xl mb-3 flex items-center justify-center overflow-hidden"
          style={{
            aspectRatio: '3 / 2',
            background: dataUrl ? '#050308' : 'rgba(255,158,184,0.08)',
            border: `1.5px dashed ${USER_PILLAR.color}88`,
          }}>
          {dataUrl ? (
            <img src={dataUrl} alt="preview" className="max-h-full max-w-full object-contain"/>
          ) : (
            <div className="text-center">
              <p className="font-['Cinzel']" style={{ color: USER_PILLAR.color, fontSize: 24 }}>+</p>
              <p className="font-['Outfit'] uppercase tracking-[0.22em]"
                style={{ color: USER_PILLAR.color, fontSize: 9 }}>Tap to add image</p>
            </div>
          )}
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile}
          style={{ display: 'none' }} data-testid="peace-forge-image-input"/>

        <label className="block mb-2">
          <span className="font-['Outfit'] uppercase tracking-[0.26em]"
            style={{ color: USER_PILLAR.color, fontSize: 9 }}>Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="peace-forge-name-input"
            className="w-full mt-1 rounded-lg px-3 py-2 font-['Cinzel'] outline-none"
            style={{ background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${USER_PILLAR.color}44`, color: '#FFE9A0', fontSize: 13 }}/>
        </label>
        <label className="block mb-2">
          <span className="font-['Outfit'] uppercase tracking-[0.26em]"
            style={{ color: USER_PILLAR.color, fontSize: 9 }}>Tagline</span>
          <input type="text" value={brief} onChange={(e) => setBrief(e.target.value)}
            data-testid="peace-forge-tagline-input"
            className="w-full mt-1 rounded-lg px-3 py-2 font-['Cormorant_Garamond'] italic outline-none"
            style={{ background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${USER_PILLAR.color}44`, color: '#FFE9A0', fontSize: 12 }}/>
        </label>
        <label className="block mb-2">
          <span className="font-['Outfit'] uppercase tracking-[0.26em]"
            style={{ color: USER_PILLAR.color, fontSize: 9 }}>Instruction (optional)</span>
          <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)}
            rows={3}
            data-testid="peace-forge-instr-input"
            className="w-full mt-1 rounded-lg px-3 py-2 font-['Outfit'] outline-none resize-none"
            style={{ background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${USER_PILLAR.color}44`, color: '#FFE9A0', fontSize: 12 }}/>
        </label>
      </div>
      <div className="flex-shrink-0 px-3 pt-2 grid grid-cols-3 gap-2 border-t"
        style={{ borderColor: `${GOLD}22`,
          paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 18px)' }}>
        <button onClick={onCancel} data-testid="peace-forge-cancel"
          className="rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.22em]"
          style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)',
                   border: '1px solid rgba(255,255,255,0.1)', fontSize: 10 }}>Cancel</button>
        <button onClick={save} disabled={!canSave}
          data-testid="peace-forge-save"
          className="col-span-2 rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.28em]"
          style={{
            background: canSave
              ? `linear-gradient(180deg, ${USER_PILLAR.accent}, ${USER_PILLAR.color})`
              : 'rgba(255,255,255,0.04)',
            color: canSave ? '#0a0a0a' : 'rgba(255,255,255,0.4)',
            border: `1px solid ${USER_PILLAR.color}`, fontSize: 11,
            boxShadow: canSave ? `0 0 18px ${USER_PILLAR.color}66` : 'none',
            opacity: canSave ? 1 : 0.4,
          }}>Forge Into Force</button>
      </div>
    </div>
  );
};

/* ── FUSE SCHEDULE PICKER — choose duration first (Power-style) ── */
const FuseSchedulePicker = ({ onCreate }) => (
  <div className="flex-1 flex flex-col overflow-hidden" data-testid="peace-schedule-picker">
    <div className="flex-shrink-0 px-4 pt-4 pb-2 text-center">
      <p className="font-['Cinzel'] tracking-[0.28em]"
        style={{ color: GOLD_BRIGHT, fontSize: 15 }}>FORGE YOUR SCHEDULE</p>
      <p className="mt-1 font-['Cormorant_Garamond'] italic"
        style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
        Choose a duration · Build your own daily yoga · No default suggestions
      </p>
    </div>
    <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-3 pb-4">
      <div className="grid grid-cols-2 gap-2.5">
        {SCHEDULE_DURATIONS.map((d) => (
          <button key={d.id}
            type="button"
            onClick={() => onCreate(d.days)}
            data-testid={`peace-schedule-${d.id}`}
            className="rounded-xl py-4 flex flex-col items-center justify-center transition-all active:scale-95"
            style={{
              background: 'linear-gradient(160deg, rgba(197,160,89,0.16), rgba(0,0,0,0.4))',
              border: `1px solid ${GOLD}66`,
              boxShadow: `0 0 12px ${GOLD}22, inset 0 1px 0 rgba(255,255,255,0.05)`,
            }}>
            <p className="font-['Cinzel'] tracking-[0.2em]"
              style={{ color: GOLD_BRIGHT, fontSize: 15 }}>{d.label}</p>
            <p className="font-['Outfit'] uppercase tracking-[0.24em] mt-1"
              style={{ color: GOLD_SOFT, fontSize: 8 }}>{d.days} days</p>
          </button>
        ))}
      </div>
    </div>
  </div>
);

/* ── FUSE CALENDAR — grid of day boxes; behaves as Schedule (edit) or Fuse (practice) via `mode` ── */
const FuseCalendar = ({ schedule, onOpenDay, onReset, mode }) => {
  const days = Array.from({ length: schedule.durationDays }, (_, i) => i);
  const isFuse = mode === 'fuse';
  const nextIdx = days.find(i => !schedule.days?.[i]?.done);
  const activeIdx = nextIdx === undefined ? schedule.durationDays - 1 : nextIdx;
  const completed = days.filter(i => schedule.days?.[i]?.done).length;
  const daysWithYogas = days.filter(i => (schedule.days?.[i]?.yogas || []).length > 0).length;
  const cols = schedule.durationDays <= 21 ? 5 : schedule.durationDays <= 30 ? 6 : 7;
  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid={`peace-${mode}-calendar`}>
      <div className="flex-shrink-0 px-4 pt-3 pb-1 flex items-center justify-between">
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: GOLD_BRIGHT, fontSize: 14 }}>
          {isFuse ? `FUSE · ${schedule.durationDays} DAYS` : `SCHEDULE · ${schedule.durationDays} DAYS`}
        </p>
        <button type="button" onClick={onReset}
          data-testid="peace-fuse-reset-btn"
          className="rounded-full px-2 py-0.5 font-['Outfit'] uppercase tracking-[0.18em]"
          style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(255,255,255,0.1)', fontSize: 8 }}>
          Change
        </button>
      </div>
      <p className="flex-shrink-0 font-['Outfit'] uppercase tracking-[0.24em] text-center pb-2 px-3"
        style={{ color: GOLD_SOFT, fontSize: 9 }}
        data-testid="peace-fuse-subtitle">
        {isFuse
          ? `${completed} / ${schedule.durationDays} fused · Tap a day to practice`
          : `${daysWithYogas} / ${schedule.durationDays} planned · Add or remove yogas anytime`}
      </p>
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {days.map((i) => {
            const day = schedule.days?.[i] || {};
            const done = !!day.done;
            const assigned = (day.yogas || []).length;
            const isActive = i === activeIdx;
            const bg = done
              ? `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`
              : isActive ? `linear-gradient(180deg, rgba(197,160,89,0.28), rgba(197,160,89,0.06))`
              : 'rgba(255,255,255,0.03)';
            const border = done ? `1px solid ${GOLD_BRIGHT}`
              : isActive ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.08)';
            return (
              <button key={i} onClick={() => onOpenDay(i)}
                data-testid={`peace-cal-day-${i}`}
                className="aspect-square rounded-xl flex flex-col items-center justify-center transition-all active:scale-95"
                style={{
                  background: bg, border,
                  boxShadow: isActive ? `0 0 12px ${GOLD}66` : 'none',
                  minHeight: 48,
                }}>
                {done ? <Check size={18} color="#0a0a0a" strokeWidth={3}/> : null}
                <span className="font-['Cinzel'] mt-0.5"
                  style={{ color: done ? '#0a0a0a' : isActive ? GOLD_BRIGHT : 'rgba(255,255,255,0.55)',
                    fontSize: 14, lineHeight: 1 }}>{i + 1}</span>
                {assigned > 0 && !done && (
                  <span className="font-['Outfit'] uppercase tracking-[0.12em] mt-0.5"
                    style={{ color: GOLD_BRIGHT, fontSize: 7 }}>
                    {assigned} yoga
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ── FUSE USER DAY — two modes controlled by `mode` prop:
   • 'schedule' → yoga picker grid + planned yoga list with time-edit only (no ▶ start)
   • 'fuse'     → tile grid of only planned yogas; tap to start; auto-fuses when all complete */
const FuseUserDay = ({ dayIdx, schedule, userYoga, defaultTimer, onChange, onStart, onDone, mode }) => {
  const day = schedule.days?.[dayIdx] || { yogas: [], done: false, sessions: [] };
  const isFuse = mode === 'fuse';
  const allChoices = [...YOGA_STYLES, ...userYoga];
  const [editingYogaId, setEditingYogaId] = useState(null);

  const patchDay = (patch) => onChange({
    ...schedule,
    days: { ...(schedule.days || {}), [dayIdx]: { ...day, ...patch } },
  });

  const toggle = (id) => {
    const has = (day.yogas || []).some(y => y.id === id);
    if (has) {
      patchDay({ yogas: day.yogas.filter(y => y.id !== id) });
    } else {
      patchDay({
        yogas: [
          ...(day.yogas || []),
          { id, active: Math.round(defaultTimer.active / 60), breathing: Math.round(defaultTimer.breathing / 60) },
        ],
      });
    }
  };

  const setYogaTime = (id, a, b) => {
    patchDay({
      yogas: day.yogas.map(y => y.id === id
        ? { ...y, active: Math.max(5, Math.min(60, a || y.active)),
            breathing: Math.max(5, Math.min(60, b || y.breathing)) }
        : y),
    });
  };

  const clear = () => patchDay({ yogas: [], done: false, sessions: [] });
  const sealManually = () => { patchDay({ done: true }); pingAuraFuse('force'); };

  const doneYogaIds = new Set((day.sessions || []).map(s => s.id));

  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid="peace-fuse-day">
      <div className="flex-shrink-0 px-4 pt-3 pb-2 flex items-center justify-between">
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: GOLD_BRIGHT, fontSize: 14 }}>DAY {dayIdx + 1}</p>
        <p className="font-['Outfit'] uppercase tracking-[0.2em]"
          style={{ color: GOLD_SOFT, fontSize: 9 }}>
          {isFuse
            ? `${doneYogaIds.size} / ${(day.yogas || []).length} done`
            : `${(day.yogas || []).length} selected`}
        </p>
      </div>

      {/* ─────── PRACTICE / FUSE MODE ─────── */}
      {isFuse && (
        <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
          <p className="font-['Cormorant_Garamond'] italic text-center mb-2"
            style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
            Tap a yoga tile to begin
          </p>
          {(day.yogas || []).length === 0 ? (
            <p className="text-center font-['Outfit'] mt-4"
              style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
              No yogas planned for this day.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {(day.yogas || []).map((y) => {
                const s = allChoices.find(c => c.id === y.id);
                if (!s) return null;
                const done = doneYogaIds.has(y.id);
                return (
                  <div key={y.id} className="relative" data-testid={`peace-practice-tile-${y.id}`}>
                    <StyleCard
                      style={s}
                      active={done}
                      onSelect={() => onStart(y.id, { active: y.active * 60, breathing: y.breathing * 60 }, dayIdx)}/>
                    <div className="absolute top-1 left-1 rounded-full px-1.5 py-0.5 font-['Cinzel']"
                      style={{ background: 'rgba(0,0,0,0.7)', color: GOLD_BRIGHT,
                        border: `1px solid ${GOLD}66`, fontSize: 8 }}>
                      {y.active}+{y.breathing}m
                    </div>
                    {done && (
                      <div className="absolute top-1 right-1 rounded-full flex items-center justify-center"
                        style={{
                          width: 20, height: 20,
                          background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
                          boxShadow: `0 0 8px ${GOLD}`,
                        }}>
                        <Check size={12} color="#0a0a0a" strokeWidth={3}/>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─────── PLANNING / SCHEDULE MODE ─────── */}
      {!isFuse && (
        <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
          {/* Selected yoga time-editor list (compact) */}
          {(day.yogas || []).length > 0 && (
            <div className="mb-2 rounded-lg px-2 py-2"
              style={{ background: 'rgba(197,160,89,0.08)', border: `1px solid ${GOLD}44` }}
              data-testid="peace-fuse-selected-list">
              <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1"
                style={{ color: GOLD, fontSize: 8 }}>Planned Yogas</p>
              {day.yogas.map((y) => {
                const s = allChoices.find(c => c.id === y.id);
                if (!s) return null;
                const isUser = s.pillar === 'User';
                const pcol = isUser ? USER_PILLAR.color : PEACE_PILLARS[s.pillar].color;
                const isEditing = editingYogaId === y.id;
                return (
                  <div key={y.id} className="flex items-center gap-1.5 mb-1 last:mb-0"
                    data-testid={`peace-fuse-plan-${y.id}`}>
                    <span className="font-['Cinzel'] flex-1 truncate"
                      style={{ color: pcol, fontSize: 10 }}>{s.name}</span>
                    {isEditing ? (
                      <>
                        <input type="number" min={5} max={60} defaultValue={y.active}
                          onBlur={(e) => setYogaTime(y.id, +e.target.value, y.breathing)}
                          data-testid={`peace-fuse-plan-active-${y.id}`}
                          className="w-9 rounded px-1 py-0.5 text-center font-['Cinzel']"
                          style={{ background: 'rgba(0,0,0,0.4)', border: `1px solid ${GOLD}55`,
                            color: '#FFE9A0', fontSize: 10 }}/>
                        <span style={{ color: GOLD_SOFT, fontSize: 8 }}>+</span>
                        <input type="number" min={5} max={60} defaultValue={y.breathing}
                          onBlur={(e) => setYogaTime(y.id, y.active, +e.target.value)}
                          data-testid={`peace-fuse-plan-breath-${y.id}`}
                          className="w-9 rounded px-1 py-0.5 text-center font-['Cinzel']"
                          style={{ background: 'rgba(0,0,0,0.4)', border: `1px solid ${GOLD}55`,
                            color: '#FFE9A0', fontSize: 10 }}/>
                        <button onClick={() => setEditingYogaId(null)}
                          className="rounded-full px-1.5 py-0.5 font-['Cinzel']"
                          style={{ background: GOLD, color: '#0a0a0a', fontSize: 8 }}>OK</button>
                      </>
                    ) : (
                      <button type="button" onClick={() => setEditingYogaId(y.id)}
                        data-testid={`peace-fuse-edit-time-${y.id}`}
                        className="rounded-full px-2 py-0.5 font-['Cinzel'] uppercase"
                        style={{ background: 'rgba(255,255,255,0.04)', color: GOLD_BRIGHT,
                          border: `1px solid ${GOLD}55`, fontSize: 8 }}>
                        {y.active}+{y.breathing} ✎
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Yoga grid (3-col like main selector) */}
          <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1"
            style={{ color: GOLD, fontSize: 8 }}>Choose Yoga</p>
          <div className="grid grid-cols-3 gap-1.5">
            {allChoices.map((s) => {
              const on = (day.yogas || []).some(y => y.id === s.id);
              return (
                <div key={s.id} className="relative">
                  <StyleCard style={s} active={on} onSelect={() => toggle(s.id)}/>
                  {on && (
                    <div className="absolute top-1 right-1 rounded-full flex items-center justify-center"
                      style={{
                        width: 20, height: 20,
                        background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
                        boxShadow: `0 0 8px ${GOLD}`,
                      }}>
                      <Check size={12} color="#0a0a0a" strokeWidth={3}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom dock */}
      <div className="flex-shrink-0 px-3 pt-2 grid grid-cols-3 gap-2 border-t"
        style={{ borderColor: `${GOLD}22`,
          paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 18px)' }}>
        <button onClick={clear} data-testid="peace-fuse-clear"
          className="rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.22em]"
          style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)',
                   border: '1px solid rgba(255,255,255,0.1)', fontSize: 10 }}>Clear</button>
        {isFuse ? (
          <button onClick={sealManually} disabled={day.done || !(day.yogas || []).length}
            data-testid="peace-fuse-seal"
            className="col-span-2 rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.28em]"
            style={{
              background: day.done ? 'rgba(197,160,89,0.15)'
                : `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
              color: day.done ? GOLD : '#0a0a0a',
              border: `1px solid ${GOLD}`, fontSize: 10,
              opacity: (day.done || !(day.yogas || []).length) ? 0.5 : 1,
              boxShadow: (day.done || !(day.yogas || []).length) ? 'none' : `0 0 14px ${GOLD}66`,
            }}>{day.done ? '✓ Fused' : 'Mark Day Fused'}</button>
        ) : (
          <button onClick={onDone}
            data-testid="peace-schedule-done-btn"
            className="col-span-2 rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.28em]"
            style={{
              background: `linear-gradient(180deg, ${GOLD_BRIGHT}, ${GOLD_SOFT})`,
              color: '#0a0a0a', fontSize: 11,
              border: `1px solid ${GOLD}`,
              boxShadow: `0 0 16px ${GOLD}55`,
            }}>
            ✓ Done
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Main modal ── */
export const ThePeace = ({ onClose }) => {
  const [screen, setScreen] = useState('selector'); // selector | session | reflection | schedule | calendar | userday | forge
  const [selected, setSelected] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [stats, setStats] = useState(null);
  const [userYoga, setUserYoga] = useState(readUserYoga);
  const [timer, setTimer] = useState(readTimer);
  const [schedule, setSchedule] = useState(readSchedule);
  const [dayIdx, setDayIdx] = useState(0);
  // 'schedule' = edit mode (Schedule button); 'fuse' = practice mode (Fuse button)
  const [calMode, setCalMode] = useState('schedule');
  // Tracks the currently-running Fuse practice: which day + which yoga in the plan
  const [practiceCtx, setPracticeCtx] = useState(null);

  // Back walks: sub-screen → selector → close. (Was broken — hook was
  // called with wrong signature so hardware back did nothing.)
  useHistoryClose(true, useCallback(() => {
    if (screen !== 'selector') {
      setScreen('selector');
      setSelected(null);
      setSessionData(null);
      setPracticeCtx(null);
      try { window.history.pushState({ __modal: Date.now() }, ''); } catch (_) {}
    } else {
      onClose();
    }
  }, [screen, onClose]));

  // Derived Force stats — schedule-scoped so they reset whenever the user
  // changes their selected days / creates a new schedule.
  const scheduleStats = useMemo(() => {
    if (!schedule) return { total_sessions: 0, total_minutes: 0, streak_days: 0 };
    const dayVals = Object.values(schedule.days || {});
    const sessions = dayVals.reduce((n, d) => n + ((d.sessions || []).length), 0);
    const minutes = dayVals.reduce((n, d) => n + (d.sessions || []).reduce(
      (m, s) => m + Math.round((s.active_sec + s.breathing_sec) / 60), 0), 0);
    let streak = 0;
    for (let i = 0; i < schedule.durationDays; i++) {
      if (schedule.days?.[i]?.done) streak += 1;
      else break;
    }
    return { total_sessions: sessions, total_minutes: minutes, streak_days: streak };
  }, [schedule]);

  const allStylesById = useMemo(() => {
    const map = { ...YOGA_BY_ID };
    userYoga.forEach(u => { map[u.id] = u; });
    return map;
  }, [userYoga]);

  const saveUserYoga = (arr) => { setUserYoga(arr); writeUserYoga(arr); };
  const saveTimer = (t) => { setTimer(t); writeTimer(t); };
  const saveSchedule = (s) => { setSchedule(s); writeSchedule(s); };
  const resetSchedule = () => { setSchedule(null); writeSchedule(null); };

  const handleDeleteUser = (id) => saveUserYoga(userYoga.filter(u => u.id !== id));

  /* ── Fuse a yoga into the schedule mid-session (works whether or not user
     started from the schedule flow). Creates a 7-day schedule if none exists,
     appends a session record to the next-active day, and auto-seals it when
     every planned yoga for that day is done. */
  const handleFuseNow = (style, elapsed) => {
    let sched = schedule;
    if (!sched) {
      sched = { durationDays: 7, days: {} };
    }
    // Find next-active day (first not-done)
    let targetDay = 0;
    for (let i = 0; i < sched.durationDays; i++) {
      if (!sched.days?.[i]?.done) { targetDay = i; break; }
    }
    const d = sched.days?.[targetDay] || { yogas: [], done: false, sessions: [] };
    // Ensure the yoga is in the plan (add with the current timer if absent)
    const hasYoga = (d.yogas || []).some(y => y.id === style.id);
    const yogas = hasYoga ? d.yogas : [
      ...(d.yogas || []),
      { id: style.id,
        active: Math.round(timer.active / 60),
        breathing: Math.round(timer.breathing / 60) },
    ];
    const sessions = [...(d.sessions || []), {
      id: style.id,
      active_sec: elapsed.active_sec,
      breathing_sec: elapsed.breathing_sec,
      at: new Date().toISOString(),
    }];
    const doneIds = new Set(sessions.map(s => s.id));
    const allDone = yogas.length > 0 && yogas.every(y => doneIds.has(y.id));
    const next = {
      ...sched,
      days: { ...(sched.days || {}), [targetDay]: { ...d, yogas, sessions, done: d.done || allDone } },
    };
    saveSchedule(next);
    setPracticeCtx(null);
    stopSpeech();
    onClose();
  };

  // Load preferred style + stats on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [profRes, statsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/peace/profile`),
          axios.get(`${BACKEND_URL}/api/peace/stats`),
        ]);
        if (cancelled) return;
        if (profRes.data?.preferred_style) setSelected(profRes.data.preferred_style);
        setStats(statsRes.data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const styleObj = useMemo(() => selected ? allStylesById[selected] : null, [selected, allStylesById]);
  const handleBegin = async () => {
    if (!selected) return;
    // Persist preference (fire-and-forget)
    axios.post(`${BACKEND_URL}/api/peace/profile`, { preferred_style: selected })
      .catch(() => {});
    setScreen('session');
  };

  // Helper — record the completed Fuse practice into the schedule
  const commitFusePractice = (data) => {
    if (!practiceCtx || !schedule) return;
    const d = schedule.days?.[practiceCtx.dayIdx] || { yogas: [], sessions: [] };
    const sessions = [...(d.sessions || []), {
      id: practiceCtx.yogaId,
      active_sec: data.active_sec,
      breathing_sec: data.breathing_sec,
      at: new Date().toISOString(),
    }];
    // Auto-seal the day when every planned yoga has been completed at least once
    const doneIds = new Set(sessions.map(s => s.id));
    const allDone = (d.yogas || []).every(y => doneIds.has(y.id)) && (d.yogas || []).length > 0;
    saveSchedule({
      ...schedule,
      days: {
        ...(schedule.days || {}),
        [practiceCtx.dayIdx]: { ...d, sessions, done: d.done || allDone },
      },
    });
    setPracticeCtx(null);
  };

  const handleSessionEnd = (data) => {
    setSessionData(data);
    // If this was a Fuse practice run, commit it (and auto-fuse the day if complete)
    if (data?.completed) commitFusePractice(data);
    setScreen('reflection');
  };

  const handleReflectionSubmit = async ({ reflection, clarity_rating }) => {
    try {
      await axios.post(`${BACKEND_URL}/api/peace/log`, {
        style_id: styleObj.id,
        pillar: styleObj.pillar,
        duration_sec: sessionData.duration_sec,
        active_sec: sessionData.active_sec,
        breathing_sec: sessionData.breathing_sec,
        completed: sessionData.completed,
        clarity_rating,
        reflection,
      });
    } catch { /* noop */ }
    // If this was a Fuse practice run, return to the day view; else close modal
    if (practiceCtx) { setScreen('userday'); setSessionData(null); setPracticeCtx(null); }
    else onClose();
  };

  const handleSkip = async () => {
    // Still log the session — just without reflection/clarity
    try {
      await axios.post(`${BACKEND_URL}/api/peace/log`, {
        style_id: styleObj.id,
        pillar: styleObj.pillar,
        duration_sec: sessionData.duration_sec,
        active_sec: sessionData.active_sec,
        breathing_sec: sessionData.breathing_sec,
        completed: sessionData.completed,
      });
    } catch { /* noop */ }
    if (practiceCtx) { setScreen('userday'); setSessionData(null); setPracticeCtx(null); }
    else onClose();
  };

  return (
    <div className="fixed inset-0 z-[220] flex flex-col"
      style={{ background: BG }}
      data-testid="peace-modal">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2.5 border-b"
        style={{ borderColor: `${GOLD}22` }}>
        <button
          type="button"
          onClick={() => {
            if (screen === 'session') {
              // Confirm exit — instead of alert, treat as end-early
              stopSpeech();
              setSessionData({
                completed: false, active_sec: 0, breathing_sec: 0, duration_sec: 0,
              });
              onClose();
            } else if (screen === 'reflection') {
              handleSkip();
            } else if (screen === 'forge' || screen === 'calendar' || screen === 'schedule') {
              setScreen('selector');
            } else if (screen === 'userday') {
              setScreen('calendar');
            } else {
              onClose();
            }
          }}
          data-testid="peace-close-btn"
          className="relative z-10 flex-shrink-0 h-8 px-3 rounded-full flex items-center gap-1 whitespace-nowrap active:scale-95 transition-transform"
          style={{ border: '1px solid #5DD4FF8C', background: '#5DD4FF14', boxShadow: '0 0 10px #5DD4FF40', color: '#5DD4FF' }}>
          <ChevronLeft size={16}/>
          <span className="font-['Outfit'] uppercase" style={{ fontSize: 11, letterSpacing: '0.18em' }}>Back</span>
        </button>
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: '#FFE9A0', fontSize: 13, textShadow: `0 0 10px ${GOLD}55` }}>
          FORCE
        </p>
        <span className="w-14"/>
      </div>

      {/* Body */}
      {screen === 'selector' && (
        <Selector selected={selected} onSelect={setSelected} onBegin={handleBegin}
          stats={scheduleStats}
          userYoga={userYoga}
          timer={timer}
          onEditTimer={saveTimer}
          onOpenForge={() => setScreen('forge')}
          onOpenSchedule={() => { setCalMode('schedule'); setScreen(schedule ? 'calendar' : 'schedule'); }}
          onOpenFuse={() => { setCalMode('fuse'); setScreen(schedule ? 'calendar' : 'schedule'); }}
          onDeleteUser={handleDeleteUser}/>
      )}
      {screen === 'session' && styleObj && (
        <Session style={styleObj} timer={timer} onEnd={handleSessionEnd}
          onCancel={() => setScreen('selector')}
          onFuseNow={handleFuseNow}/>
      )}
      {screen === 'reflection' && styleObj && sessionData && (
        <Reflection style={styleObj} sessionData={sessionData}
          onSubmit={handleReflectionSubmit} onSkip={handleSkip}/>
      )}
      {screen === 'forge' && (
        <ForgeYoga
          onSave={(newYoga) => { saveUserYoga([...userYoga, newYoga]); setScreen('selector'); }}
          onCancel={() => setScreen('selector')}/>
      )}
      {screen === 'schedule' && (
        <FuseSchedulePicker onCreate={(days) => {
          saveSchedule({ durationDays: days, days: {} });
          setScreen('calendar');
        }}/>
      )}
      {screen === 'calendar' && schedule && (
        <FuseCalendar
          mode={calMode}
          schedule={schedule}
          onOpenDay={(i) => { setDayIdx(i); setScreen('userday'); }}
          onReset={() => { resetSchedule(); setScreen('schedule'); }}/>
      )}
      {screen === 'calendar' && !schedule && (
        <FuseSchedulePicker onCreate={(days) => {
          saveSchedule({ durationDays: days, days: {} });
        }}/>
      )}
      {screen === 'userday' && schedule && (
        <FuseUserDay
          mode={calMode}
          dayIdx={dayIdx}
          schedule={schedule}
          userYoga={userYoga}
          defaultTimer={timer}
          onChange={saveSchedule}
          onDone={() => setScreen('calendar')}
          onStart={(id, tOverride, thisDayIdx) => {
            setSelected(id);
            if (tOverride) setTimer(tOverride);
            setPracticeCtx({ dayIdx: thisDayIdx ?? dayIdx, yogaId: id });
            setScreen('session');
          }}/>
      )}

      {/* Ambient CSS animations */}
      <style>{`
        @keyframes peaceGlow {
          0%, 100% { filter: drop-shadow(0 0 20px ${GOLD}77); }
          50% { filter: drop-shadow(0 0 34px ${GOLD_BRIGHT}); }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

/* Peace tile icon — lotus + concentric aura */
export const PeaceTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 8px rgba(216,168,114,0.7))' }}>
    <defs>
      <linearGradient id="pceIcon" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#F0DAA3"/>
        <stop offset="0.55" stopColor="#D8A872"/>
        <stop offset="1" stopColor="#7A5A3A"/>
      </linearGradient>
    </defs>
    {/* Outer aura ring */}
    <circle cx="12" cy="12" r="10.4" fill="none"
      stroke="url(#pceIcon)" strokeWidth="0.7" opacity="0.55"/>
    {/* Lotus petals */}
    <path d="M12 17 C 6 17, 3 13, 3 9.5 C 6.5 9.5, 9.5 11, 12 13 C 14.5 11, 17.5 9.5, 21 9.5 C 21 13, 18 17, 12 17 Z"
      fill="none" stroke="url(#pceIcon)" strokeWidth="1.2" strokeLinejoin="round"/>
    <path d="M12 13 C 8 11, 6.5 6.5, 7.5 3 C 10 5, 12 8, 12 11 Z"
      fill="none" stroke="url(#pceIcon)" strokeWidth="1.0"/>
    <path d="M12 13 C 16 11, 17.5 6.5, 16.5 3 C 14 5, 12 8, 12 11 Z"
      fill="none" stroke="url(#pceIcon)" strokeWidth="1.0"/>
    {/* Dot / bindu */}
    <circle cx="12" cy="18.5" r="1.3" fill="url(#pceIcon)"/>
  </svg>
);

export default ThePeace;
